import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Skeleton,
    SkeletonItem,
} from '@fluentui/react-components';
import React, { useCallback, useMemo, useState } from 'react';
import {
    useContentSkeletonStyles,
    useCustomerUpdatesDialogStyles,
} from './CustomerUpdatesDialog.styles';
import { Dismiss24Regular } from '@fluentui/react-icons';
import CustomerUpdatesDialogButtons from '../CustomerUpdatesDialogButtons/CustomerUpdatesDialogButtons';
import { CustomerInfo } from '../../../../model/customerWithUpdates';
import {
    CustomerItemStatus,
} from '../../../../store/features/customerUpdates/customerUpdatesSlice';
import { useTranslation } from 'react-i18next';
import { UnableToNotifySubform } from './subforms/UnableToNotifySubform';
import { BackSubmitButtons } from '../CustomerUpdatesDialogButtons/BackSubmitButtons';
import FeedbackMessageSubform from './subforms/FeedbackMessageSubform';
import { CustomerInfoSection } from './CustomerInfoSection';
import { useCustomerUpdatesDialogSubformState } from './useCustomerUpdatesDialogSubformState';

const ContentSkeleton: React.FC = () => {
    const styles = useContentSkeletonStyles();

    return (
        <Skeleton className={styles.root}>
            <SkeletonItem className={styles.item} shape="rectangle" />
        </Skeleton>
    );
};

export type CustomerUpdatesDialogView =
    | 'initial'
    | 'unableToNotify'
    | 'messageFeedback';

export interface CustomerUpdatesDialogProps {
    open?: boolean;
    icon?: React.ReactNode;
    customer?: CustomerInfo;
    status?: CustomerItemStatus;
    content?: React.ReactNode;
    view?: CustomerUpdatesDialogView;
    isFlightTeamBoard?: boolean;
    onNotified: () => void;
    onUnableToNotify: (reason: string | null) => void;
    onMessageFeedback?: (message: string) => void;
    onOpenChange?: (open: boolean) => void;
    onViewChange?: (view: CustomerUpdatesDialogView) => void;
}

const CustomerUpdatesDialog: React.FC<CustomerUpdatesDialogProps> = ({
    open,
    icon,
    customer,
    status,
    content,
    view: controlledView,
    isFlightTeamBoard,
    onNotified,
    onUnableToNotify,
    onMessageFeedback,
    onOpenChange,
    onViewChange,
}) => {
    const styles = useCustomerUpdatesDialogStyles();
    const { t } = useTranslation();

    const [uncontrolledView, setUncontrolledView] =
        useState<CustomerUpdatesDialogView>('initial');

    const view = useMemo(
        () =>
            controlledView === undefined ? uncontrolledView : controlledView,
        [controlledView, uncontrolledView]
    );

    const onViewChangeCallback = useCallback(
        (newView: CustomerUpdatesDialogView) => {
            if (controlledView === undefined) {
                setUncontrolledView(newView);
            }

            onViewChange?.(newView);
        },
        [controlledView, onViewChange]
    );

    const {
        selectedUnableToNotifyReason,
        setSelectedUnableToNotifyReason,
        feedbackMessage,
        setFeedbackMessage,
        disableUnableToNotifyButton,
        disableFeedbackButton,
        onFeedbackSubmitCallback,
    } = useCustomerUpdatesDialogSubformState(
        view,
        onMessageFeedback
    );

    const resetView = useCallback(() => {
        onViewChangeCallback('initial');
    }, [onViewChangeCallback]);

    const onOpenChangeCallback = useCallback(
        (open: boolean) => {
            resetView();
            onOpenChange?.(open);
        },
        [resetView, onOpenChange]
    );

    return (
        <Dialog
            modalType="non-modal"
            open={open}
            onOpenChange={(_, data) => onOpenChangeCallback(data.open)}
        >
            <DialogSurface className={styles.dialogSurface}>
                <DialogBody className={styles.dialogBody}>
                    <DialogTitle
                        className={styles.dialogTitle}
                        action={
                            <DialogTrigger action="close">
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                />
                            </DialogTrigger>
                        }
                    >
                        {icon && (
                            <div className={styles.dialogTitleIcon}>{icon}</div>
                        )}
                        {customer && (
                            <CustomerInfoSection
                                customer={customer}
                                status={status}
                            />
                        )}
                    </DialogTitle>
                    <DialogContent className={styles.dialogContent}>
                        {!content ? (
                            <ContentSkeleton />
                        ) : (
                            <>
                                {content}
                                <div className={styles.dialogSubform}>
                                    {view === 'unableToNotify' && (
                                        <UnableToNotifySubform
                                            onSelect={(reason) =>
                                                setSelectedUnableToNotifyReason(
                                                    reason
                                                )
                                            }
                                        />
                                    )}
                                    {view === 'messageFeedback' && (
                                        <FeedbackMessageSubform
                                            value={feedbackMessage}
                                            onChange={setFeedbackMessage}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </DialogContent>
                    {isFlightTeamBoard && (
                        <DialogActions
                            position="end"
                            className={styles.actions}
                        >
                            {view === 'initial' && (
                                <>
                                    {onMessageFeedback && (
                                        <Button
                                            onClick={() =>
                                                onViewChangeCallback(
                                                    'messageFeedback'
                                                )
                                            }
                                        >
                                            {t(
                                                'CustomerDialog_AddFeedbackButton'
                                            )}
                                        </Button>
                                    )}
                                    <CustomerUpdatesDialogButtons
                                        notifiedStatus={status}
                                        onNotified={onNotified}
                                        onUnableToNotify={() =>
                                            onViewChangeCallback(
                                                'unableToNotify'
                                            )
                                        }
                                    />
                                </>
                            )}
                            {view === 'unableToNotify' && (
                                <BackSubmitButtons
                                    submitLabel={t(
                                        'CustomerDialog_UnableToNotifyButton'
                                    )}
                                    onBack={resetView}
                                    onSubmit={() =>
                                        onUnableToNotify(
                                            selectedUnableToNotifyReason ?? null
                                        )
                                    }
                                    submitDisabled={disableUnableToNotifyButton}
                                />
                            )}
                            {view === 'messageFeedback' && (
                                <BackSubmitButtons
                                    submitLabel={t(
                                        'CustomerDialog_AddFeedbackButton'
                                    )}
                                    onBack={resetView}
                                    onSubmit={onFeedbackSubmitCallback}
                                    submitDisabled={disableFeedbackButton}
                                    closeDialogOnSubmit={false}
                                />
                            )}
                        </DialogActions>
                    )}
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

CustomerUpdatesDialog.displayName = 'CustomerUpdatesDialog';

export default CustomerUpdatesDialog;
